import { Modal, Alert, Button, Checkbox, Input, InputNumber, Select, Spin, Tag, message } from 'antd'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { LoadingOutlined, ReloadOutlined } from '@ant-design/icons'
import styles from './whatsapp-connect.module.scss'
import './add-channel.module.scss'
import {
    getWaAccount,
    getWaGroups,
    getWaQr,
    loginWaAccount,
    updateWaConfig,
    waAccountStatusText,
    WaApiError,
} from '@/service/whatsappPersonal/api'
import type { WaAccountItem, WaGroup } from '@/service/whatsappPersonal/types'
import { getBotListApi } from '@/service/bot/api/getBotList'
import type { BotItem } from '@/service/bot/types/getBotList'

const POLL_INTERVAL = 3000
const GROUP_PAGE_SIZE = 50
const MAX_GROUPS = 100

type Phase = 'connect' | 'config'

interface WhatsappConnectProps {
    open: boolean
    channelId: string | null
    /** 打开时希望进入的步骤；未连接时总会先停留在连接步骤 */
    initialPhase?: Phase
    onClose: () => void
    /** 账号状态/配置有变化，需要刷新列表 */
    onChanged?: () => void
}

const sleep = (ms: number, signal: AbortSignal) =>
    new Promise<void>((resolve) => {
        const t = setTimeout(resolve, ms)
        signal.addEventListener('abort', () => { clearTimeout(t); resolve() }, { once: true })
    })

const errText = (e: any) => {
    const err = e as WaApiError
    return `${err?.message || 'Request failed'}${err?.requestId ? ` (request_id: ${err.requestId})` : ''}`
}

export const waStatusClass = (status?: string | null) => {
    switch (status) {
        case 'WORKING': return styles['st-ok']
        case 'STARTING':
        case 'PENDING':
        case 'SCAN_QR_CODE': return styles['st-wait']
        case 'FAILED': return styles['st-bad']
        default: return styles['st-off']
    }
}
export const waStatusTagClass = styles.statusTag

const WhatsappConnect: React.FC<WhatsappConnectProps> = ({ open, channelId, initialPhase = 'connect', onClose, onChanged }) => {
    const [item, setItem] = useState<WaAccountItem | null>(null)
    const [phase, setPhase] = useState<Phase>('connect')
    const [qrSrc, setQrSrc] = useState('')
    const [polling, setPolling] = useState(false)
    const [pollError, setPollError] = useState('')
    const [loginLoading, setLoginLoading] = useState(false)
    const pollCtrlRef = useRef<AbortController | null>(null)

    // ---------- config state ----------
    const [groups, setGroups] = useState<WaGroup[]>([])
    const [groupOffset, setGroupOffset] = useState(0)
    const [groupHasMore, setGroupHasMore] = useState(true)
    const [groupLoading, setGroupLoading] = useState(false)
    const [groupError, setGroupError] = useState('')
    const [groupKeyword, setGroupKeyword] = useState('')
    const [selected, setSelected] = useState<Set<string>>(new Set())
    const [displayName, setDisplayName] = useState('')
    const [botList, setBotList] = useState<BotItem[]>([])
    const [robotId, setRobotId] = useState<number | undefined>(undefined)
    const [robotVersionId, setRobotVersionId] = useState<number | null>(0)
    const [saving, setSaving] = useState(false)
    const configCtrlRef = useRef<AbortController | null>(null)
    const configInitedRef = useRef(false)

    const status = item?.account?.status
    const bindingStatus = item?.binding?.status
    const isDeleting = bindingStatus === 'deleting' || bindingStatus === 'deleted' || status === 'DELETING' || status === 'DELETED'

    const stopPolling = useCallback(() => {
        pollCtrlRef.current?.abort()
        pollCtrlRef.current = null
        setPolling(false)
    }, [])

    /** 串行轮询详情：上一轮结束后间隔 3 秒再发下一轮；SCAN_QR_CODE 时每轮刷新二维码 */
    const startPolling = useCallback((id: string) => {
        pollCtrlRef.current?.abort()
        const ctrl = new AbortController()
        pollCtrlRef.current = ctrl
        setPolling(true)
        setPollError('')

        const run = async () => {
            let failures = 0
            while (!ctrl.signal.aborted) {
                try {
                    const detail = await getWaAccount(id, ctrl.signal)
                    if (ctrl.signal.aborted) return
                    failures = 0
                    setPollError('')
                    setItem(detail)
                    const s = detail.account?.status
                    const b = detail.binding?.status
                    if (b === 'deleting' || b === 'deleted' || s === 'DELETING' || s === 'DELETED') { setQrSrc(''); break }
                    if (s === 'WORKING') { setQrSrc(''); setPhase('config'); break }
                    if (s === 'FAILED' || s === 'STOPPED') { setQrSrc(''); break }
                    if (s === 'SCAN_QR_CODE') {
                        try {
                            const qr = await getWaQr(id, ctrl.signal)
                            if (!ctrl.signal.aborted) setQrSrc(`data:${qr.mimetype};base64,${qr.data}`)
                        } catch (e: any) {
                            if (e?.canceled || ctrl.signal.aborted) return
                            // 二维码失败可能是状态已变化，下一轮详情会决定显示什么
                        }
                    } else {
                        setQrSrc('')
                    }
                } catch (e: any) {
                    if (e?.canceled || ctrl.signal.aborted) return
                    failures += 1
                    setPollError(errText(e))
                    // 管理 Key 错误或连续异常时停止自动轮询，改为手动刷新
                    if (e?.status === 401 || e?.status === 404 || failures >= 3) break
                }
                await sleep(POLL_INTERVAL, ctrl.signal)
            }
            if (pollCtrlRef.current === ctrl) {
                pollCtrlRef.current = null
                setPolling(false)
            }
        }
        run()
    }, [])

    // 打开 / 切换账号 / 关闭
    useEffect(() => {
        if (open && channelId) {
            setItem(null)
            setQrSrc('')
            setPhase('connect')
            setGroupLoading(false)
            configInitedRef.current = false
            startPolling(channelId)
        }
        return () => {
            stopPolling()
            configCtrlRef.current?.abort()
            configCtrlRef.current = null
        }
    }, [open, channelId, startPolling, stopPolling])

    const handleReconnect = async () => {
        if (!channelId) return
        setLoginLoading(true)
        try {
            const res = await loginWaAccount(channelId)
            setItem(res)
            onChanged?.()
        } catch (e: any) {
            message.error(errText(e))
        } finally {
            setLoginLoading(false)
            // 无论成功与否都重新查询一次真实状态
            startPolling(channelId)
        }
    }

    // ---------- config ----------
    const loadGroups = useCallback(async (reset = false) => {
        if (!channelId) return
        const ctrl = configCtrlRef.current ?? new AbortController()
        configCtrlRef.current = ctrl
        const offset = reset ? 0 : groupOffset
        setGroupLoading(true)
        setGroupError('')
        try {
            const page = await getWaGroups(channelId, GROUP_PAGE_SIZE, offset, ctrl.signal)
            if (ctrl.signal.aborted) return
            setGroups((prev) => {
                const base = reset ? [] : prev
                const seen = new Set(base.map((g) => g.id))
                return [...base, ...page.groups.filter((g) => !seen.has(g.id))]
            })
            setGroupOffset(offset + GROUP_PAGE_SIZE)
            setGroupHasMore(page.groups.length >= GROUP_PAGE_SIZE)
        } catch (e: any) {
            if (e?.canceled) return
            setGroupError(errText(e))
        } finally {
            if (!ctrl.signal.aborted) setGroupLoading(false)
        }
    }, [channelId, groupOffset])

    // 进入配置步骤时初始化一次
    useEffect(() => {
        if (!open || phase !== 'config' || !item || configInitedRef.current) return
        configInitedRef.current = true
        setSelected(new Set(item.account?.group_ids || []))
        setDisplayName(item.account?.display_name || item.binding.channel_name || '')
        setRobotId(item.binding.robot_id || undefined)
        setRobotVersionId(item.binding.robot_version_id || 0)
        setGroups([])
        setGroupOffset(0)
        setGroupHasMore(true)
        setGroupKeyword('')
        loadGroups(true)
        getBotListApi({ page: 1, page_size: 999 } as any)
            .then((res) => setBotList(res.data?.items || []))
            .catch(() => message.error('Failed to fetch bot list'))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, phase, item])

    useEffect(() => {
        // initialPhase === 'config' 只是意图；只有 WORKING 才真正进入配置
        if (initialPhase === 'config' && status === 'WORKING') setPhase('config')
    }, [initialPhase, status])

    const botOptions = useMemo(
        () => botList.filter((b) => b.dm_robot?.robot_id).map((b) => ({ label: b.name, value: b.dm_robot.robot_id })),
        [botList]
    )

    const filteredGroups = useMemo(() => {
        const kw = groupKeyword.trim().toLowerCase()
        if (!kw) return groups
        return groups.filter((g) => g.subject?.toLowerCase().includes(kw) || g.id.includes(kw))
    }, [groups, groupKeyword])

    const groupNameMap = useMemo(() => new Map(groups.map((g) => [g.id, g.subject])), [groups])

    const toggleGroup = (id: string, checked: boolean) => {
        setSelected((prev) => {
            const next = new Set(prev)
            if (checked) {
                if (next.size >= MAX_GROUPS) {
                    message.warning(`At most ${MAX_GROUPS} groups can be selected`)
                    return prev
                }
                next.add(id)
            } else {
                next.delete(id)
            }
            return next
        })
    }

    const handleSave = async () => {
        if (!channelId || !item) return
        if (!displayName.trim()) {
            message.warning('Please enter Display Name')
            return
        }
        if (robotId && !Number(robotVersionId)) {
            message.warning('Please enter Robot Version ID')
            return
        }
        const bot = botList.find((b) => b.dm_robot?.robot_id === robotId)
        setSaving(true)
        try {
            const res = await updateWaConfig(channelId, {
                display_name: displayName.trim(),
                group_ids: Array.from(selected),
                robot_id: robotId || 0,
                robot_version_id: robotId ? Number(robotVersionId) || 0 : 0,
                robot_name: robotId ? bot?.name ?? item.binding.robot_name ?? '' : '',
            })
            setItem(res)
            message.success('Configuration saved')
            onChanged?.()
            onClose()
        } catch (e: any) {
            // 失败沿同一 PUT 重试，不自动回滚
            message.error(errText(e))
        } finally {
            setSaving(false)
        }
    }

    // ---------- render ----------
    const renderConnect = () => {
        if (!item && polling) {
            return <div className={styles.statusBox}><Spin indicator={<LoadingOutlined spin />} /><div>Loading account status...</div></div>
        }
        if (isDeleting) {
            return <div className={styles.statusBox}><div>This account is {bindingStatus === 'deleted' || status === 'DELETED' ? 'unbound' : 'being unbound'}. Connection and configuration are disabled.</div></div>
        }
        if (bindingStatus === 'pending' && !item?.account) {
            return (
                <div className={styles.statusBox}>
                    <div>Account creation is not finished yet.</div>
                    <Button type="primary" className={styles.actionBtn} loading={loginLoading} onClick={handleReconnect}>Resume</Button>
                    {pollError && <div className={styles.errorText}>{pollError}</div>}
                </div>
            )
        }
        switch (status) {
            case 'SCAN_QR_CODE':
                return (
                    <div className={styles.statusBox}>
                        <div className={styles.qrWrap}>
                            {qrSrc ? <img src={qrSrc} alt="WhatsApp QR code" /> : <Spin />}
                        </div>
                        <div>Open WhatsApp on your phone → Settings → Linked devices → Link a device, then scan this code.</div>
                        <div className={styles.hint}>The QR code refreshes automatically. Closing this window does not unbind the account.</div>
                        {pollError && <div className={styles.errorText}>{pollError}</div>}
                    </div>
                )
            case 'WORKING':
                return (
                    <div className={styles.statusBox}>
                        <div>WhatsApp is connected.</div>
                        <Button type="primary" className={styles.actionBtn} onClick={() => setPhase('config')}>Configure groups & bot</Button>
                    </div>
                )
            case 'FAILED':
            case 'STOPPED':
                return (
                    <div className={styles.statusBox}>
                        <span className={`${styles.statusTag} ${waStatusClass(status)}`}>{waAccountStatusText(status)}</span>
                        <div>The WhatsApp session is {status === 'FAILED' ? 'failed' : 'stopped'}.</div>
                        <Button type="primary" className={styles.actionBtn} loading={loginLoading} onClick={handleReconnect}>Reconnect</Button>
                        {pollError && <div className={styles.errorText}>{pollError}</div>}
                    </div>
                )
            case 'STARTING':
            case 'PENDING':
                return (
                    <div className={styles.statusBox}>
                        <Spin indicator={<LoadingOutlined spin />} />
                        <div>Connecting to WhatsApp...</div>
                        {pollError && <div className={styles.errorText}>{pollError}</div>}
                    </div>
                )
            default:
                return (
                    <div className={styles.statusBox}>
                        <div>Status: {status || 'Unknown'}</div>
                        {pollError && <div className={styles.errorText}>{pollError}</div>}
                    </div>
                )
        }
    }

    const renderConfig = () => (
        <div>
            <div className={styles.sectionTitle}>Display Name</div>
            <Input value={displayName} maxLength={255} onChange={(e) => setDisplayName(e.target.value)} placeholder="Display name" />

            <div className={styles.sectionTitle}>
                <span>Groups (whitelist) · selected {selected.size}/{MAX_GROUPS}</span>
                <Button size="small" type="text" icon={<ReloadOutlined />} onClick={() => loadGroups(true)} disabled={groupLoading}>Refresh</Button>
            </div>
            <Input
                allowClear
                placeholder="Filter loaded groups"
                value={groupKeyword}
                onChange={(e) => setGroupKeyword(e.target.value)}
                style={{ marginBottom: 8 }}
            />
            <div className={styles.groupList}>
                {filteredGroups.map((g) => (
                    <Checkbox key={g.id} checked={selected.has(g.id)} onChange={(e) => toggleGroup(g.id, e.target.checked)}>
                        {g.subject || '(no name)'}<span className={styles.groupId}>{g.id}</span>
                    </Checkbox>
                ))}
                {!groupLoading && groups.length === 0 && !groupError && (
                    <div className={styles.hint}>No groups found. Make sure this WhatsApp number has joined the target groups, then refresh.</div>
                )}
                {groupError && <div className={styles.errorText}>{groupError}</div>}
                {groupLoading && <div style={{ textAlign: 'center', padding: 8 }}><Spin size="small" /></div>}
                {!groupLoading && groupHasMore && groups.length > 0 && (
                    <Button type="link" size="small" onClick={() => loadGroups(false)}>Load more</Button>
                )}
            </div>
            {selected.size > 0 && (
                <div className={styles.selectedTags}>
                    {Array.from(selected).map((id) => (
                        <Tag key={id} closable onClose={() => toggleGroup(id, false)}>
                            {groupNameMap.get(id) || id}
                        </Tag>
                    ))}
                </div>
            )}
            {selected.size === 0 && (
                <Alert
                    data-testid="wa-no-group-hint"
                    type="warning"
                    showIcon
                    style={{ marginTop: 6 }}
                    message="No group selected: group messages will not be sent to the bot. 群未勾选：该群消息不会转给机器人。"
                />
            )}
            <div className={styles.hint} style={{ marginTop: 6 }}>Only text messages in selected groups are handled. An empty list means no group is handled. Private chats are not answered. 私聊消息不会回复。</div>

            <div className={styles.sectionTitle}>Bot</div>
            <Select
                className={styles.selectWrapper}
                style={{ width: '100%' }}
                placeholder="Select a bot (optional)"
                allowClear
                showSearch
                options={botOptions}
                value={robotId}
                filterOption={(input, option) => String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                onChange={(val) => {
                    setRobotId(val)
                    const bot = botList.find((b) => b.dm_robot?.robot_id === val)
                    setRobotVersionId(bot?.dm_robot?.version_id ?? 0)
                }}
            />
            <div className={styles.sectionTitle}>Robot Version ID</div>
            <InputNumber
                className={styles.numberInput}
                style={{ width: '100%', marginBottom: 24 }}
                min={0}
                precision={0}
                disabled={!robotId}
                value={robotVersionId}
                onChange={(v) => setRobotVersionId(v)}
            />
        </div>
    )

    const footer = phase === 'config'
        ? [
            <Button key="back" onClick={() => setPhase('connect')}>Back</Button>,
            <Button key="save" type="primary" loading={saving} disabled={isDeleting} onClick={handleSave}>Save</Button>,
        ]
        : [
            <Button key="refresh" icon={<ReloadOutlined />} disabled={polling || !channelId} onClick={() => channelId && startPolling(channelId)}>Refresh status</Button>,
            <Button key="close" type="primary" onClick={onClose}>Close</Button>,
        ]

    return (
        <Modal
            title={`WhatsApp Personal · ${item?.account?.display_name || item?.binding?.channel_name || channelId || ''}`}
            open={open}
            onCancel={onClose}
            footer={footer}
            centered
            destroyOnClose
            maskClosable={false}
            width={700}
            className="add-channel-modal"
        >
            {phase === 'config' && status === 'WORKING' ? renderConfig() : renderConnect()}
        </Modal>
    )
}

export default WhatsappConnect
